import cn from 'classnames';
import React, { FC, useState } from 'react';

import { Bars } from '@.partials';
import { Filter3, FilterData } from '@.ui';
import { analyticsStoreSelectors } from '@/Store';
import { PreferableProductsStatistic } from '@/Types/analytics';

import { BAR_FACTOR, MINI_TOP_STEP_VALUE, TOP_STEP_VALUE } from '../FavoriteHeartChart/constants';
import S from './styles.module.scss';

interface PreferableProductsChartProps {
    data: PreferableProductsStatistic;
    mini?: boolean;
}

enum FilterType {
    AGE = 'age',
    SEX = 'sex',
}

export const PreferableProductsChart: FC<PreferableProductsChartProps> = ({ data, mini = false }) => {
    const [ageIds, setAgeIds] = useState<number[]>([]);
    const [genderId, setGenderId] = useState<number | undefined>(-1);

    const filterBestCombinationIngredients = analyticsStoreSelectors.use.filterPreferableProducts();

    const stepValue = mini ? MINI_TOP_STEP_VALUE : TOP_STEP_VALUE;

    const getFilterValues = (item: FilterData, filterType: FilterType) => {
        if (filterType === FilterType.AGE) {
            const newAgeIds = item.age_id && typeof item.age_id === 'string' ? item.age_id.split(',').map((id) => Number(id)) : undefined;

            if (!newAgeIds) {
                return { ageIds: undefined, genderId };
            }

            setAgeIds(newAgeIds);

            return {
                ageIds: newAgeIds,
                genderId,
            };
        }

        if (filterType === FilterType.SEX) {
            const newGenderId = item.sex_id ? Number(item.sex_id) : undefined;

            setGenderId(newGenderId);

            return { ageIds, genderId: newGenderId };
        }

        return { ageIds: undefined, genderId: undefined };
    };

    const onChangeFilter = (item: FilterData, filterType: FilterType) => {
        filterBestCombinationIngredients(getFilterValues(item, filterType));
    };

    const height = data.dataset.length * stepValue * BAR_FACTOR;

    return (
        <div className={cn(S.preferable_products, { [S['preferable_products-mini']]: mini })}>
            <h3 className={cn(S.title, { [S['title-mini']]: mini })}>Top Products Chosen by Users</h3>
            {!mini && (
                <div className={S.filtersWrapper}>
                    <Filter3
                        className={S.filter}
                        onChange={(age) => onChangeFilter(age, FilterType.AGE)}
                        fromData={{
                            ages: data.ages,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All ages"
                    >
                        <Filter3.Ages visibleLabel={''} visibleName="All ages" multiple />
                    </Filter3>
                    <Filter3
                        className={S.filter}
                        onChange={(sex) => onChangeFilter(sex, FilterType.SEX)}
                        fromData={{
                            sexes: data.genders,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All genders"
                    >
                        <Filter3.Sexes visibleLabel={''} visibleName="All genders" />
                    </Filter3>
                </div>
            )}

            <Bars data={data.dataset} height={height} isHorizontal axisDataName="Products" isHideFractionalTicks mini={mini} />
        </div>
    );
};
